import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { Wallet, Lightning, CreditCard, Trash, CheckCircle, Warning } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { flag, fmtData, fmtEUR, fmtDate, countryName } from "../lib/format";
import Header from "../components/Header";
import Footer from "../components/Footer";

function UsageRing({ total, used }) {
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  return (
    <div>
      <div className="h-2 rounded-full bg-ink-100 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-cobalt-500 to-cobalt-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex justify-between mt-2 text-xs text-ink-500">
        <span className="font-semibold text-ink-700">{fmtData(used)}</span>
        <span>{fmtData(total)}</span>
      </div>
    </div>
  );
}

const statusPill = {
  fulfilled: "bg-mint-soft text-mint",
  paid: "bg-cobalt-50 text-cobalt-600",
  provisioning: "bg-sun-soft text-ink-900",
  pending_payment: "bg-ink-100 text-ink-500",
  provisioning_failed: "bg-red-50 text-destructive",
  cancelled: "bg-ink-100 text-ink-500",
};

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [esims, setEsims] = useState([]);
  const [orders, setOrders] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [banner, setBanner] = useState(null); // { kind:'success'|'error'|'info', text }
  const [thresholdInput, setThresholdInput] = useState("");
  const [amountInput, setAmountInput] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingCard, setSavingCard] = useState(false);

  const loadWallet = async () => {
    const { data } = await api.get("/me/wallet");
    setWallet(data);
    setThresholdInput(String(data?.auto_topup_threshold_eur ?? 5));
    setAmountInput(String(data?.auto_topup_amount_eur ?? 25));
    return data;
  };

  useEffect(() => {
    Promise.all([api.get("/me/esims"), api.get("/me/orders"), loadWallet()])
      .then(([e, o]) => { setEsims(e.data); setOrders(o.data); })
      .finally(() => setLoading(false));
  }, []);

  // Handle redirect back from a wallet top-up / save-card. The PayFly flow
  // already redirects to /checkout/success?session_id=…, so here we only need
  // to acknowledge ?card_saved=… for the explicit "Save card" button on the
  // dashboard which sends the user through a normal NeptunePay top-up (a
  // standard €5 top-up that also flags `startRecurring: true`).
  useEffect(() => {
    const cardSaved = params.get("card_saved");
    if (cardSaved === "1") {
      setBanner({ kind: "success", text: t("dash.cardSavedOk", "Card saved. Auto top-up is now ready.") });
      params.delete("card_saved"); setParams(params, { replace: true });
      loadWallet();
    } else if (cardSaved === "0") {
      setBanner({ kind: "info", text: t("dash.cardSavedCancel", "Card save cancelled.") });
      params.delete("card_saved"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAuto = async () => {
    const next = !wallet?.auto_topup;
    await api.post("/me/wallet/auto-topup", { enabled: next });
    setWallet({ ...wallet, auto_topup: next });
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.post("/me/wallet/settings", {
        auto_topup_threshold_eur: parseFloat(thresholdInput),
        auto_topup_amount_eur: parseFloat(amountInput),
      });
      setWallet({ ...wallet, ...data });
      setBanner({ kind: "success", text: t("dash.settingsSaved", "Auto top-up settings saved.") });
    } catch (e) {
      setBanner({ kind: "error", text: e.response?.data?.detail || "Could not save." });
    } finally { setSavingSettings(false); }
  };

  const startSaveCard = async () => {
    setSavingCard(true);
    try {
      const { data } = await api.post("/me/wallet/save-card", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setBanner({ kind: "error", text: e.response?.data?.detail || "Card save not available. Make sure NEPTUNEPAY_API_KEY is configured." });
      setSavingCard(false);
    }
  };

  // Show a small hint that this flow includes a €5 top-up.
  const saveCardLabel = (loading) => loading ? "…" : t("dash.saveCard", "Save card (€5 top-up)");

  const removeCard = async () => {
    await api.delete("/me/wallet/saved-card");
    await loadWallet();
    setBanner({ kind: "info", text: t("dash.cardRemoved", "Saved card removed.") });
  };

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      <Header />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="overline mb-3">{t("common.welcome")}, {user?.name || user?.email?.split("@")[0]}</div>
        <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tight">{t("dash.title")}</h1>
        <p className="mt-3 text-ink-700">{t("dash.subtitle")}</p>

        {/* Pay & Fly Wallet */}
        <section className="mt-10">
          <div className="card-soft !rounded-3xl p-6 lg:p-8 bg-gradient-to-br from-cobalt-500 to-cobalt-600 text-white relative overflow-hidden" data-testid="wallet-card">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-coral/30 blob animate-blob pointer-events-none" />
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Wallet size={20} weight="bold" />
                  <span className="text-xs uppercase tracking-widest font-semibold opacity-80">{t("payfly.walletTitle")}</span>
                </div>
                <div className="font-display font-bold text-5xl tracking-tight">{fmtEUR(wallet?.balance_eur || 0)}</div>
                {!wallet?.exists && <div className="mt-2 text-sm opacity-80">{t("payfly.walletEmpty")}</div>}
                <label className="mt-4 flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!wallet?.auto_topup} onChange={toggleAuto} className="w-4 h-4 accent-coral" data-testid="wallet-auto-topup-toggle" />
                  <span>{t("payfly.autoTopup")}</span>
                </label>
              </div>
              <Link to="/payfly" className="btn bg-white text-cobalt-600 hover:bg-coral hover:text-white text-base shadow-pop" data-testid="wallet-add-credit">
                <Lightning size={16} weight="fill" /> {t("payfly.addCredit")}
              </Link>
            </div>
          </div>
        </section>

        {/* Auto top-up settings panel — visible when the user has opted in. */}
        {wallet?.auto_topup && (
          <section className="mt-6" data-testid="auto-topup-settings">
            <div className="card-soft p-6 lg:p-8 grid lg:grid-cols-[1fr_auto] gap-6 items-end">
              <div>
                <div className="overline mb-3">{t("dash.autoTopupSettings", "Auto top-up settings")}</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-widest text-ink-500">{t("dash.threshold", "Recharge when below")}</span>
                    <div className="mt-1 flex items-center input-soft !p-0">
                      <span className="pl-4 text-ink-500">€</span>
                      <input
                        type="number" min="1" max="100" step="1"
                        value={thresholdInput}
                        onChange={(e) => setThresholdInput(e.target.value)}
                        className="flex-1 px-2 py-3 outline-none bg-transparent border-0"
                        data-testid="auto-topup-threshold-input"
                      />
                    </div>
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-widest text-ink-500">{t("dash.topupAmount", "Recharge amount")}</span>
                    <div className="mt-1 flex items-center input-soft !p-0">
                      <span className="pl-4 text-ink-500">€</span>
                      <input
                        type="number" min="5" max="500" step="1"
                        value={amountInput}
                        onChange={(e) => setAmountInput(e.target.value)}
                        className="flex-1 px-2 py-3 outline-none bg-transparent border-0"
                        data-testid="auto-topup-amount-input"
                      />
                    </div>
                  </label>
                </div>
                {/* Saved card status */}
                <div className="mt-5 rounded-2xl bg-ink-50 border border-ink-100 p-4 flex items-center justify-between gap-4">
                  {wallet?.recurring_token ? (
                    <>
                      <div className="flex items-center gap-3">
                        <CreditCard size={22} weight="bold" className="text-cobalt-500" />
                        <div>
                          <div className="font-semibold text-sm">
                            {(wallet.card_brand || "CARD").toUpperCase()} {wallet.card_last4 ? `•••• ${wallet.card_last4}` : ""}
                          </div>
                          <div className="text-xs text-ink-500">
                            {wallet.card_exp_month && wallet.card_exp_year ? (
                              <>{t("dash.cardExp", "Exp")} {String(wallet.card_exp_month).padStart(2, "0")}/{String(wallet.card_exp_year).slice(-2)}</>
                            ) : t("dash.cardSavedShort", "Saved via NeptunePay")}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeCard}
                        className="btn-ghost text-xs text-destructive hover:bg-red-50"
                        data-testid="remove-saved-card"
                      >
                        <Trash size={14} /> {t("dash.removeCard", "Remove")}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 text-sm text-ink-700">
                        <Warning size={20} weight="bold" className="text-coral-deep" />
                        <span>{t("dash.noCardOnFile", "No card on file — auto top-up will only send reminder emails until you save a card.")}</span>
                      </div>
                      <button
                        type="button"
                        onClick={startSaveCard}
                        disabled={savingCard}
                        className="btn-outline text-sm whitespace-nowrap"
                        data-testid="save-card-btn"
                      >
                        <CreditCard size={14} weight="bold" /> {saveCardLabel(savingCard)}
                      </button>
                    </>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={saveSettings}
                disabled={savingSettings}
                className="btn-primary text-sm h-fit"
                data-testid="save-auto-topup-settings"
              >
                {savingSettings ? "…" : t("dash.saveSettings", "Save settings")}
              </button>
            </div>
          </section>
        )}

        {banner && (
          <div
            className={`mt-4 rounded-2xl p-4 flex items-start gap-3 text-sm ${
              banner.kind === "success" ? "bg-mint-soft text-mint" :
              banner.kind === "error" ? "bg-red-50 text-destructive" : "bg-cobalt-50 text-cobalt-600"
            }`}
            data-testid="dash-banner"
          >
            {banner.kind === "success" ? <CheckCircle size={18} weight="bold" /> : <Warning size={18} weight="bold" />}
            <span className="flex-1">{banner.text}</span>
            <button onClick={() => setBanner(null)} className="text-xs underline">×</button>
          </div>
        )}

        <section className="mt-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display font-semibold text-2xl tracking-tight">{t("dash.activeEsims")}</h2>
            <Link to="/plans" className="btn-ghost text-sm" data-testid="dash-browse">{t("dash.browse")}</Link>
          </div>
          {loading && <div className="text-ink-500">{t("common.loading")}</div>}
          {!loading && esims.length === 0 && (
            <div className="card-soft p-12 text-center" data-testid="dash-empty">
              <div className="text-5xl mb-3">✈️</div>
              <p className="text-ink-700 mb-6">{t("dash.noEsims")}</p>
              <Link to="/plans" className="btn-primary inline-flex">{t("dash.browse")}</Link>
            </div>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {esims.map((e) => (
              <div key={e.id} className="card-soft p-6 flex flex-col" data-testid={`esim-card-${e.id}`}>
                <Link to={`/esim/${e.id}`} className="block">
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{flag(e.plan_snapshot.country_iso2)}</span>
                      <div>
                        <div className="font-display font-semibold text-lg tracking-tight">{e.plan_snapshot.name}</div>
                        <div className="text-xs text-ink-500">{countryName(e.plan_snapshot.country_iso2, i18n.language, e.plan_snapshot.country)}</div>
                      </div>
                    </div>
                    <span className="pill bg-mint-soft text-mint">{e.status}</span>
                  </div>
                  <UsageRing total={e.data_total_mb} used={e.data_used_mb} />
                  <div className="mt-4 flex items-center justify-between text-xs">
                    <span className="text-ink-500">{t("dash.expires")}</span>
                    <span className="font-semibold">{fmtDate(e.expiry_date)}</span>
                  </div>
                </Link>
                <Link to={`/topup/${e.id}`} className="btn-coral !py-2.5 text-sm mt-5 w-full" data-testid={`esim-topup-${e.id}`}>
                  ⚡ {t("common.topup")}
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16">
          <h2 className="font-display font-semibold text-2xl tracking-tight mb-6">{t("dash.recentOrders")}</h2>
          <div className="card-soft overflow-hidden">
            <table className="w-full text-sm" data-testid="dash-orders-table">
              <thead className="bg-cobalt-50 text-xs uppercase tracking-widest text-cobalt-600">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">{t("dash.orderId")}</th>
                  <th className="text-left px-6 py-3 font-semibold">{t("common.plans")}</th>
                  <th className="text-left px-6 py-3 font-semibold">{t("dash.amount")}</th>
                  <th className="text-left px-6 py-3 font-semibold">{t("common.status")}</th>
                  <th className="text-left px-6 py-3 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && (<tr><td colSpan={5} className="px-6 py-8 text-center text-ink-500">—</td></tr>)}
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-ink-100 hover:bg-sky-soft/40">
                    <td className="px-6 py-3 font-mono text-xs">{o.id.slice(0, 8)}…</td>
                    <td className="px-6 py-3">{o.plan_snapshot?.name}</td>
                    <td className="px-6 py-3 font-semibold">{fmtEUR(o.amount)}</td>
                    <td className="px-6 py-3">
                      <span className={`pill ${statusPill[o.status] || "bg-ink-100 text-ink-500"}`}>{o.status.replace(/_/g, " ")}</span>
                    </td>
                    <td className="px-6 py-3 text-ink-500">{fmtDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
